import React from 'react';
import { InlineLoader } from './InlineLoader';

interface PdfPreviewProps {
  file?: File | null;
  url?: string | null;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Lightweight PDF first-page preview using the browser's built-in PDF renderer.
 * We load the document with `#page=1` and hide toolbars. This avoids extra deps.
 */
export const PdfPreview: React.FC<PdfPreviewProps> = ({ file, url, className = '', style = {} }) => {
  const objectUrl = React.useMemo(() => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  React.useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  // Navigation and layout state
  const [page, setPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number | null>(null);
  const [pdfLoaded, setPdfLoaded] = React.useState<boolean>(false);
  const [pageAspectRatio, setPageAspectRatio] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showBlockedNote, setShowBlockedNote] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Measure container width responsively
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Set initial width synchronously
    setContainerWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const srcBase = objectUrl || url || '';
  const isRemoteUrl = Boolean(url && !objectUrl);

  if (!srcBase) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`} style={style}>
        <p className="text-xs text-gray-600">No PDF</p>
      </div>
    );
  }

  const canGoPrev = page > 1;
  const canGoNext = pageCount ? page < pageCount : true;

  // pdf.js dynamic loader (CDN) to avoid local iframe/object restrictions
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const renderTaskRef = React.useRef<any>(null);
  const pdfDocRef = React.useRef<any>(null);

  const loadPdfJs = React.useCallback(async () => {
    const w = window as any;
    if (w.pdfjsLib) return w.pdfjsLib;
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      // Prefer legacy UMD build that exposes window.pdfjsLib (v2.x is stable for globals)
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Fallback to unpkg CDN if cdnjs fails (e.g., blocked)
        const fallback = document.createElement('script');
        fallback.src = 'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.min.js';
        fallback.async = true;
        fallback.onload = () => resolve();
        fallback.onerror = () => reject(new Error('Failed to load pdf.js'));
        document.head.appendChild(fallback);
      };
      document.head.appendChild(script);
    });
    const lib = (window as any).pdfjsLib;
    if (lib) {
      lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
    }
    return lib;
  }, []);

  // Open document with pdf.js
  React.useEffect(() => {
    let cancelled = false;
    async function openDoc() {
      try {
        const pdfjsLib = await loadPdfJs();
        if (!pdfjsLib) return;

        setIsLoading(true);
        let loadingTask: any;
        if (file instanceof File) {
          // Prefer object URL so pdf.js can handle range requests internally
          const localUrl = URL.createObjectURL(file);
          loadingTask = pdfjsLib.getDocument(localUrl);
        } else if (url) {
          // Dev proxy to bypass CORS when running locally
          const effectiveUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? `/api/pdf-proxy?url=${encodeURIComponent(url)}`
            : url;
          try {
            const resp = await fetch(effectiveUrl, { mode: 'cors' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const dataBuf = await resp.arrayBuffer();
            loadingTask = pdfjsLib.getDocument({ data: dataBuf });
          } catch (e) {
            // Fallback: load by URL with disabled range/stream
            loadingTask = pdfjsLib.getDocument({ url, disableRange: true, disableStream: true, withCredentials: false });
          }
        } else {
          setErrorMessage('No PDF data');
          return;
        }
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages || null);
        setPage(1);
        try {
          const first = await pdf.getPage(1);
          const vp = first.getViewport({ scale: 1 });
          const ratio = vp.height / vp.width;
          if (isFinite(ratio) && ratio > 0.3 && ratio < 3.5) {
            setPageAspectRatio(ratio);
          }
        } catch {}
        setPdfLoaded(true);
        setErrorMessage(null);
        setIsLoading(false);
      } catch (e) {
        // Keep UI usable even if parsing fails
        setPageCount(null);
        setErrorMessage('Unable to render PDF');
        if (isRemoteUrl) setShowBlockedNote(true);
        setIsLoading(false);
      }
    }
    openDoc();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) renderTaskRef.current.cancel();
    };
  }, [file, url, loadPdfJs]);

  // Render current page to canvas
  React.useEffect(() => {
    async function renderPage() {
      const pdf = pdfDocRef.current;
      const canvas = canvasRef.current;
      if (!pdf || !canvas || !containerWidth) return;
      const pageObj = await pdf.getPage(page);
      const viewport = pageObj.getViewport({ scale: 1 });
      const scale = containerWidth / viewport.width;
      const scaled = pageObj.getViewport({ scale });
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // HiDPI scaling for crisp rendering
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(scaled.width * dpr);
      canvas.height = Math.floor(scaled.height * dpr);
      canvas.style.width = `${Math.floor(scaled.width)}px`;
      canvas.style.height = `${Math.floor(scaled.height)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const newRatio = scaled.height / scaled.width;
      if (!pageAspectRatio || Math.abs(newRatio - pageAspectRatio) > 0.01) {
        setPageAspectRatio(newRatio);
      }
      if (renderTaskRef.current) renderTaskRef.current.cancel();
      renderTaskRef.current = pageObj.render({ canvasContext: ctx, viewport: scaled });
      try {
        await renderTaskRef.current.promise;
      } catch (err) {
        // cancelled
        if (err && (err as any).name !== 'RenderingCancelledException') {
          setErrorMessage('Render failed');
        }
      }
      setIsLoading(false);
    }
    renderPage();
  }, [page, containerWidth, pdfLoaded]);

  // Compute height from current canvas ratio (fallback to A-series if 0)
  const canvas = canvasRef.current;
  const ratioFromCanvas = canvas && canvas.width ? Math.max(0.5, Math.min(3, canvas.height / canvas.width)) : null;
  const ratio = pageAspectRatio ?? ratioFromCanvas ?? 1.4142;
  const computedHeight = Math.max(320, Math.round(containerWidth * ratio));

  return (
    <div ref={containerRef} className={`w-full rounded-lg bg-gray-100 relative ${className}`} style={style}>
      <div style={{ width: '100%', height: computedHeight }} className="overflow-hidden rounded-lg border border-black flex items-center justify-center">
        {errorMessage ? (
          isRemoteUrl && url ? (
            <iframe
              src={`https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`}
              title="PDF preview (fallback)"
              className="w-full h-full"
              style={{ border: 'none' }}
            />
          ) : (
            <div className="text-center text-xs text-gray-600">
              <div className="mb-2">{errorMessage}</div>
              <a href={objectUrl || url || '#'} target="_blank" rel="noopener noreferrer" className="inline-block px-2 py-1 border border-black rounded bg-white hover:bg-gray-50">
                Open PDF
              </a>
              {showBlockedNote && (
                <div className="mt-2 text-[10px] text-gray-500 max-w-[90%] mx-auto">
                  Remote host blocks embedding or CORS; preview uses fallback. Download to view if blocked.
                </div>
              )}
            </div>
          )
        ) : (
          <canvas ref={canvasRef} className="max-w-full max-h-full" />
        )}

        {isLoading && !errorMessage && (
          <div className="absolute inset-0 flex items-center justify-center bg-transparent" aria-label="Loading PDF">
            <InlineLoader size="md" />
          </div>
        )}
      </div>

      <div className="absolute top-3 right-3 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
        {page}{pageCount ? `/${pageCount}` : ''}
      </div>

      <button
        type="button"
        aria-label="Previous page"
        onClick={() => canGoPrev && setPage(p => Math.max(1, p - 1))}
        disabled={!canGoPrev}
        className={`absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-black bg-white shadow flex items-center justify-center ${
          canGoPrev ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
        }`}
      >
        ‹
      </button>

      <button
        type="button"
        aria-label="Next page"
        onClick={() => canGoNext && setPage(p => p + 1)}
        disabled={!canGoNext}
        className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-black bg-white shadow flex items-center justify-center ${
          canGoNext ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
        }`}
      >
        ›
      </button>
    </div>
  );
};


